import React, { useMemo, useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  YStack,
  XStack,
  useMedia,
  useWindowDimensions,
  View,
  isWeb,
  Spinner,
} from 'tamagui'
import { Modal } from '@components/Modal'
import { ScrollBar } from '@components/ScrollBar'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { CONTINUE_CARD_DIMENSIONS } from '@constants/continueCardDimensions'
import { useMediaSize } from '@hooks/useMediaSize'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Platform, NativeSyntheticEvent, NativeScrollEvent } from 'react-native'
import { isNearEnd, DEFAULT_END_REACHED_THRESHOLD } from '@utils/scrollNearEnd'

interface IEnrollmentContentsModalProps {
  open: boolean
  onClose: () => void
  lessonCards: React.ReactElement[]
  title: string
  isLoadingMoreContents?: boolean
  /** Chamado quando o scroll chega perto do fim da lista. */
  onEndReached?: () => void
  endReachedThreshold?: number
}

export function EnrollmentContentsModal({
  open,
  onClose,
  lessonCards,
  title,
  isLoadingMoreContents,
  onEndReached,
  endReachedThreshold = DEFAULT_END_REACHED_THRESHOLD,
}: IEnrollmentContentsModalProps) {
  const media = useMedia()
  const safeArea = useSafeAreaInsets()
  const { height, width } = useWindowDimensions()
  const size = useMediaSize()

  const [shouldRender, setShouldRender] = useState(false)
  const modalPaddingBottom = media.gtSm ? 10 : 50
  const modalCardsGap = media.gtMd ? 20 : 12
  const cardsPerRow = media.gtLg ? 4 : 3
  const calcScrollBarMaxHeight = media.gtSm
    ? height * 0.9
    : height - safeArea.top
  const titlePaddingVertical = media.gtSm
    ? '$size.spacing-md'
    : '$size.spacing-xs'

  const modalMaxPosition = useMemo(() => {
    if (isWeb) return undefined
    const number =
      Platform.OS === 'ios'
        ? height - safeArea.top
        : height - (safeArea.top + safeArea.bottom)
    return [number, number]
  }, [height, safeArea.top, safeArea.bottom])

  const calcModalMaxWidth = useMemo(() => {
    if (media.gtLg) return 1544
    if (media.gtMd) return 1100
    if (media.gtSm) return 608
    return width
  }, [media.gtLg, media.gtMd, media.gtSm, width])

  const setContainerWidth = () => {
    const gapTotalSpacing = modalCardsGap * cardsPerRow
    const cardsTotalWidth =
      (CONTINUE_CARD_DIMENSIONS[size].width ||
        CONTINUE_CARD_DIMENSIONS.xs.width) * cardsPerRow
    return cardsTotalWidth + gapTotalSpacing
  }

  // Pagina por distância do fim, não por encostar nele: quem arrasta a barra
  // lateral chega ao fundo, a página nova é anexada e o fim "foge" — sem isso
  // cada página exige um arrasto novo. Cobre wheel, drag da barra e touch, já
  // que todos passam pelo onScroll do ScrollBar. A sentinela continua cobrindo
  // o caso do conteúdo que cabe na tela sem gerar scroll nenhum.
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!onEndReached) return
      const { layoutMeasurement, contentOffset, contentSize } =
        event.nativeEvent

      if (
        isNearEnd({
          offsetY: contentOffset.y,
          viewportHeight: layoutMeasurement.height,
          contentHeight: contentSize.height,
          threshold: endReachedThreshold,
        })
      ) {
        onEndReached()
      }
    },
    [onEndReached, endReachedThreshold],
  )

  // Bugfix: mobile web precisa aguardar layout do modal antes de renderizar conteúdo
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setShouldRender(true), 100)
      return () => clearTimeout(timer)
    } else {
      setShouldRender(false)
    }
  }, [open])

  return (
    <Modal
      onClose={onClose}
      open={open}
      snapPointsMode={isWeb ? undefined : 'mixed'}
      snapPoints={modalMaxPosition}
      backdropOpacity={0.64}
      shouldAdapt={false}
      maxWidth={calcModalMaxWidth}
      disableDrag
    >
      <YStack
        paddingLeft="$size.spacing-xs"
        height={calcScrollBarMaxHeight}
        $gtSm={{
          padding: '$size.spacing-lg',
        }}
        flex={1}
      >
        <View
          paddingVertical={titlePaddingVertical}
          flexDirection="row"
          justifyContent="space-between"
          $gtMd={{
            paddingTop: 0,
          }}
        >
          <Dialog.Title
            color="$text-primary"
            ff="$mulishSemiBold"
            fontSize="$font-size-sm"
          >
            {title}
          </Dialog.Title>
          {media.sm && (
            <Button onPress={onClose} icon>
              <HeroIcon
                icon={['fal', 'xmark']}
                color="$icon-primary"
                size={12}
              />
            </Button>
          )}
        </View>
        <ScrollBar
          maxHeight={calcScrollBarMaxHeight}
          displayNativeIndicator={false}
          scrollBarId="continue-lessons-modal"
          onScroll={handleScroll}
        >
          <YStack paddingBottom={modalPaddingBottom}>
            <XStack
              key={`${title}-${lessonCards.length}`}
              flexWrap="wrap"
              flex={1}
              gap={modalCardsGap}
              $gtSm={{
                width: setContainerWidth(),
              }}
            >
              {shouldRender && lessonCards}
            </XStack>
            {isLoadingMoreContents && (
              <Spinner color="$spinner-strong" size="large" />
            )}
          </YStack>
        </ScrollBar>
      </YStack>
    </Modal>
  )
}
